import CircularProgress from "@mui/material/CircularProgress";
import { useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	generatePath,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import routing from "routing";
import { PathBackButton } from "src/components/Default/LekUI/BackButton";
import { OutlinedPrimaryButton } from "src/components/Default/LekUI/Button";
import { ViewBox } from "src/components/Default/Main/ViewBox";
import { FolderColorIcon } from "src/components/Folders/FolderColorIcon";
import { FolderPaginatedItems } from "src/components/Folders/FolderPaginatedItems";
import { FolderCoursesPreview } from "src/components/Folders/Previews/FolderCoursesPreview";
import { FolderExamsPreview } from "src/components/Folders/Previews/FolderExamsPreview";
import { FolderFlashcardsPreview } from "src/components/Folders/Previews/FolderFlashcardsPreview";
import { FolderNotesPreview } from "src/components/Folders/Previews/FolderNotesPreview";
import type {
	FolderItemType,
	SelectedItem,
} from "src/components/Folders/types";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { FolderSelectionBar } from "src/components/SelectionBar/FolderSelectionBar";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import { getService } from "src/helpers/isPes";
import { useDebounce } from "src/hooks/useDebounce";
import {
	useDeleteFolder,
	useGetFolder,
	useRemoveFolderItems,
} from "src/hooks/useFolders";

const TAB_TYPES = ["", "deck", "exam", "course", "note"];

export const FolderView = () => {
	const { t } = useTranslation();
	const { id, type } = useParams<{ id: string; type?: string }>();
	const history = useHistory();
	const location = useLocation();
	const { openModal } = useModals();
	const { mutateAsync: deleteFolder } = useDeleteFolder();

	const queryClient = useQueryClient();

	const [isSelectMode, setIsSelectMode] = useState(false);
	const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

	const prevFolderIdRef = useRef(id);

	useEffect(() => {
		if (prevFolderIdRef.current !== id) {
			prevFolderIdRef.current = id;
			const params = new URLSearchParams(location.search);
			if (params.has("search")) {
				params.delete("search");
				const qs = params.toString();
				history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
			}
		}
	}, [id, location.search, location.pathname, history]);

	useEffect(() => {
		return () => {
			queryClient.removeQueries({
				queryKey: ["folder", getService(), id],
			});
			queryClient.removeQueries({
				queryKey: ["folder-items", getService(), id],
			});
		};
	}, [id, queryClient]);

	const { mutateAsync: removeFolderItems } = useRemoveFolderItems();

	const toggleSelection = useCallback(
		(
			item: Record<string, unknown>,
			itemType: FolderItemType,
			folderItemId: string,
		) => {
			setSelectedItems((prev) => {
				const itemId = Number(item.id);
				const exists = prev.find(
					(i) => i.itemId === itemId && i.itemType === itemType,
				);
				if (exists) {
					return prev.filter(
						(i) => !(i.itemId === itemId && i.itemType === itemType),
					);
				}
				return [...prev, { id: folderItemId, itemId, itemType }];
			});
		},
		[],
	);

	const exitSelectMode = useCallback(() => {
		setIsSelectMode(false);
		setSelectedItems([]);
	}, []);

	const handleRemoveFromFolder = () => {
		if (selectedItems.length === 0) return;
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.removeFromFolder.title", "Usunąć z folderu?"),
			description: t(
				"modals.deleteItem.removeFromFolder.description",
				"Wybrane elementy zostaną usunięte z folderu, ale nie zostaną trwale usunięte. Nadal będziesz mieć do nich dostęp.",
			),
			confirmText: t(
				"modals.deleteItem.removeFromFolder.confirm",
				"Usuń z folderu",
			),
			onDelete: async () => {
				await removeFolderItems({
					folderId: id,
					items: selectedItems.map((i) => Number(i.id)),
				});
				exitSelectMode();
			},
		});
	};

	const handleMoveToFolder = () => {
		openModal(ModalsNames.MoveToFolderModal, {
			folderId: id,
			selectedItems,
			onSuccess: exitSelectMode,
		});
	};

	const handleCopyToFolder = () => {
		openModal(ModalsNames.MoveToFolderModal, {
			folderId: id,
			selectedItems,
			onSuccess: exitSelectMode,
			isCopyMode: true,
			multiSelect: true,
		});
	};

	const searchParams = new URLSearchParams(location.search);
	const searchValue = searchParams.get("search") || "";
	const setSearchValue = (value: string) => {
		const params = new URLSearchParams(location.search);
		if (value) {
			params.set("search", value);
		} else {
			params.delete("search");
		}
		const qs = params.toString();
		history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
	};
	const debouncedSearchValue = useDebounce(searchValue, 500);

	const currentTabIndex = type ? TAB_TYPES.indexOf(type) : 0;
	const selectedTab = currentTabIndex !== -1 ? currentTabIndex : 0;

	const handleTabChange = (val: number) => {
		const newType = TAB_TYPES[val];
		const basePath = newType
			? generatePath(routing.paths.folder, { id, type: newType })
			: generatePath(routing.paths.folder, { id }).replace(/\/$/, "");

		const qs = searchValue ? `?search=${encodeURIComponent(searchValue)}` : "";
		history.push(`${basePath}${qs}`);
	};

	const folderParams =
		!type && debouncedSearchValue ? { name: debouncedSearchValue } : undefined;
	const {
		data: folder,
		isLoading,
		isFetching,
	} = useGetFolder(id, folderParams);

	const tabs = [
		t("folder.tabs.all", "Wszystkie"),
		t("folder.tabs.flashcards", "Fiszki"),
		t("folder.tabs.exams", "Egzaminy"),
		t("folder.tabs.courses", "Kursy"),
		t("folder.tabs.notes", "Notatki"),
	];

	const searchProps = {
		placeholder: t("folder.search", "Szukaj w folderze"),
		value: searchValue,
		onChange: setSearchValue,
		onClear: () => setSearchValue(""),
		loading: isFetching && !isLoading,
	};

	const renderActionButton = () => {
		if (!type) return null;

		let btnProps: { text: string; action: () => void } | null = null;

		switch (type) {
			case "exam":
				btnProps = {
					text: t("folder.newExam", "Nowy egzamin"),
					action: () => history.push(`/egzaminy/typ?folderId=${id}`),
				};
				break;
			case "course":
				btnProps = {
					text: t("folder.newCourse", "Nowy kurs"),
					action: () => history.push(`/kursy/typ?folderId=${id}`),
				};
				break;
			case "deck":
				btnProps = {
					text: t("folder.newDeck", "Nowy zestaw"),
					action: () =>
						history.push(
							`${routing.paths.pickFlashcardsDeckChoice}?folderId=${id}`,
						),
				};
				break;
			case "note":
				btnProps = {
					text: t("folder.newNote", "Nowa notatka"),
					action: () =>
						history.push(generatePath(routing.paths.createNote, { id })),
				};
				break;
			default:
				return null;
		}

		if (!btnProps) return null;

		return (
			<OutlinedPrimaryButton
				onClick={btnProps.action}
				className="flex items-center h-100 folder-action-button"
			>
				<Plus className="icon-normal folder-action-button-icon" />
				<span className="folder-action-button-text">{btnProps.text}</span>
			</OutlinedPrimaryButton>
		);
	};

	const renderContent = () => {
		return (
			<div className="flex flex-col w-100 gap-8">
				<FolderExamsPreview
					folderId={id}
					folderName={folder?.name}
					items={folder?.items?.exam}
					isSelectMode={isSelectMode}
					selectedItems={selectedItems}
					toggleSelection={toggleSelection}
				/>
				<FolderCoursesPreview
					folderId={id}
					folderName={folder?.name}
					items={folder?.items?.course}
					isSelectMode={isSelectMode}
					selectedItems={selectedItems}
					toggleSelection={toggleSelection}
				/>
				<FolderFlashcardsPreview
					folderId={id}
					folderName={folder?.name}
					items={folder?.items?.deck}
					isSelectMode={isSelectMode}
					selectedItems={selectedItems}
					toggleSelection={toggleSelection}
				/>
				<FolderNotesPreview
					folderId={id}
					folderName={folder?.name}
					items={folder?.items?.note}
					isSelectMode={isSelectMode}
					selectedItems={selectedItems}
					toggleSelection={toggleSelection}
				/>
			</div>
		);
	};

	const menuItems = [
		{
			title: t("folder.addToFolder", "Dodaj do folderu"),
			onClick: () => {
				openModal(ModalsNames.AddFolderContentModal, {
					folderId: id,
					folderName: folder?.name,
					initialTab: type || undefined,
				});
			},
			Icon: <Plus className="icon-small" />,
			testId: "folder-menu-add-item",
		},
		{
			title: t("folder.editFolder", "Edytuj folder"),
			onClick: () => {
				openModal(ModalsNames.EditFolderModal, {
					folderId: id,
					folderName: folder?.name,
					folderColor: folder?.details?.color,
				});
			},
			Icon: <Pencil className="icon-small" />,
			testId: "folder-menu-edit-item",
		},
		{
			title: t("folder.selectMany", "Zaznacz wiele"),
			onClick: () => setIsSelectMode(true),
			Icon: <CheckSquare className="icon-small" />,
			testId: "folder-menu-select-many-item",
		},
		{
			title: t("folder.deleteFolder", "Usuń folder"),
			onClick: () => {
				openModal(ModalsNames.DeleteItemModal, {
					title: t("modals.deleteItem.folder.title", "Usunąć folder?"),
					description: t(
						"modals.deleteItem.folder.description",
						"Usunięty zostanie tylko folder — wszystkie egzaminy, kursy, zestawy fiszek i notatki, które się w nim znajdują, pozostaną na Twoim koncie.",
					),
					confirmText: t("modals.deleteItem.folder.confirm", "Usuń folder"),
					itemLabel: folder?.name,
					onDelete: async () => {
						await deleteFolder(id);
						history.push(generatePath(routing.paths.foldersList));
					},
				});
			},
			Icon: <Trash2 className="icon-small icon-error" />,
			itemTextClassName: "text-error",
			testId: "folder-menu-delete-item",
		},
	];

	return (
		<>
			<LayoutWithSearch>
				<div className="folders-items-list-container">
					{isLoading ? (
						<div className="flex-center-row w-100 p-64">
							<CircularProgress />
						</div>
					) : (
						<ViewBox
							title={folder?.name}
							icon={
								<>
									<PathBackButton />
									<FolderColorIcon
										color={folder?.details?.color}
										iconClassName="icon-normal"
									/>
								</>
							}
							renderActionButton={renderActionButton}
							menuItems={menuItems}
							tabs={tabs}
							tabTestIds={[
								"folder-tab-all",
								"folder-tab-flashcards",
								"folder-tab-exams",
								"folder-tab-courses",
								"folder-tab-notes",
							]}
							selectedTab={selectedTab}
							onTabChange={handleTabChange}
							searchProps={searchProps}
						>
							{isSelectMode && (
								<FolderSelectionBar
									selectedCount={selectedItems.length}
									onMoveToFolder={handleMoveToFolder}
									onCopyToFolder={handleCopyToFolder}
									onRemove={handleRemoveFromFolder}
									onClose={exitSelectMode}
								/>
							)}
							{type ? (
								<FolderPaginatedItems
									folderId={id}
									type={type}
									searchValue={debouncedSearchValue}
									isSelectMode={isSelectMode}
									selectedItems={selectedItems}
									toggleSelection={toggleSelection}
								/>
							) : (
								renderContent()
							)}
						</ViewBox>
					)}
				</div>
			</LayoutWithSearch>
		</>
	);
};
